import { createContext, useContext, useEffect, useState } from "react";
import { api, setToken } from "@/lib/api";
import { AUTH_TOKEN_KEY, AUTH_RETURNING_KEY } from "@/lib/constants";

const AuthContext = createContext(null);

/**
 * Best-effort helpers around localStorage that never throw (SSR-safe,
 * private-mode-safe). We use these to persist the "has-signed-in-before"
 * marker independently of the auth token.
 */
function markReturning() {
  try { window.localStorage.setItem(AUTH_RETURNING_KEY, "1"); } catch { /* noop */ }
}
function clearReturning() {
  try { window.localStorage.removeItem(AUTH_RETURNING_KEY); } catch { /* noop */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!t) { setLoading(false); return; }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // /me came back OK — this is a returning, still-authenticated user.
        // Latch the marker so we recognize them next visit even if the
        // token has since expired.
        markReturning();
      })
      .catch(() => {
        // Stale/expired token. Clear the token so future protected calls
        // don't get 401'd — but INTENTIONALLY keep the "returning" marker
        // so the Landing header shows `Log in | Open dashboard` instead
        // of `Log in | Get started`. Only an explicit logout wipes the
        // marker.
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const nudgeStorageListeners = (newValue) => {
    try {
      window.dispatchEvent(new StorageEvent("storage", { key: AUTH_TOKEN_KEY, newValue }));
    } catch (_) { /* older browsers */ }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token); setUser(data.user);
    markReturning();
    nudgeStorageListeners(data.token);
    // Return the full response so the caller can route to /verify-email
    // when `verification_required` is true.
    return { user: data.user, verificationRequired: !!data.verification_required };
  };
  const signup = async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    setToken(data.token); setUser(data.user);
    markReturning();
    nudgeStorageListeners(data.token);
    return { user: data.user, verificationRequired: !!data.verification_required };
  };
  const logout = () => {
    // Explicit user-initiated logout — wipe every trace, including the
    // returning marker. Next visit renders as a fresh visitor.
    setToken(null); setUser(null);
    clearReturning();
    nudgeStorageListeners(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, signup, logout, setUser }}>{children}</AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
